/*
 * src/components/MessageCenter/MessageCenter.jsx
 * Description: Tabular view of message center with form / filter element
 * Copyright (c) 2020 Mark Fehrenbacher
 */
import React, { useState, useEffect } from "react";
import moment from "moment";
import { DatePicker } from "antd";
import OnOutsideClick from "react-outclick";
import "antd/dist/antd.css";

import searchIcon from "../../styles/assets/message-searchicon.svg";
import sortIconAsc from "../../styles/assets/table-filter-asc-icon.svg";
import sortIconDsc from "../../styles/assets/table-filter-dsc-icon.svg";
import dropdownIcon from "../../styles/assets/btn-dropdown-icon.svg";
import closeIcon from "../../styles/assets/filter-selected-close-icon.svg";
import { useClientMessages } from "../../utils/service/service_message-center";
import downloadPdfPromise from "../../utils/service/service_pdf-download";
import { asyncWrapper, getClientStore } from "../../utils/util";

import StandardView from "./StandardView";
import MobileView from "./MobileView";
import "./messageCenter.css";

const MessageCenter = () => {
  const lastXDays = 7;
  const dynamicDateIndex = `Last ${lastXDays} Days`;
  const DateRanges = {
    Today: [moment(), moment()],
    Yesterday: [moment().subtract(1, "days"), moment()],
    [dynamicDateIndex]: [moment().subtract(lastXDays - 1, "days"), moment()],
    "This Month": [moment().startOf("month"), moment().endOf("month")],
    "Last Month": [
      moment()
        .subtract(1, "months")
        .startOf("month"),
      moment()
        .subtract(1, "months")
        .endOf("month"),
    ],
  };

  /**
   * PDF stream reader / download functionality
   * @param {*} pdfUrl String
   */
  const pdfOpenHandler = async (pdfUrl) => {
    const [err, data] = await asyncWrapper(downloadPdfPromise(pdfUrl));

    if (err) {
      return;
    }

    const file = new Blob([data], { type: "application/pdf" });
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  };

  /**
   * Set the base criteria: this applies to all queries
   */
  const { user } = getClientStore();
  const baseCriteria = {
    client: { id: user.id },
    messageType_in: [
      "transfer_fail",
      "transfer_initiated",
      "transfer_receipt",
      "transfer_request",
    ],
  };

  /**
   * Set the initial search criteria
   */
  const [searchCriteria, setSearchCriteria] = useState(
    Object.assign({}, baseCriteria, {
      createdAt_gte: DateRanges[dynamicDateIndex][0].toISOString(),
      createdAt_lte: DateRanges[dynamicDateIndex][1].toISOString(),
    })
  );

  /**
   * build the OR clause from the search input
   * @param {*} str
   */
  const buildOrClause = (str) => {
    let orClause = {};

    if (str) {
      orClause = [
        { transfer: { propertyAddress1_contains_i: str } },
        { tenant: { name_contains_i: str } },
      ];
    }

    return {
      OR: orClause,
    };
  };

  /**
   * gql hook
   */
  const { allClientMessages } = useClientMessages(searchCriteria);

  /**
   * local state
   * @TODO Refactor to useReducer()
   */
  const [availableMessageTypes, setAvailableMessageTypes] = useState([]);
  const [showMessageTypes, setShowMessageTypes] = useState(false);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [sortOrder, setOrder] = useState(false);
  const [tableData, setTableData] = useState(allClientMessages ?? []);
  const [searchKey, setSearchKey] = useState("");
  const [copyTableData, setCopyTableData] = useState([]);
  const [daysDiff, setDaysDiff] = useState(lastXDays);
  const [rangeDate, setRangeDate] = useState([
    DateRanges[dynamicDateIndex][1],
    DateRanges[dynamicDateIndex][0],
  ]);

  /**
   * This function will filter the current result on selected message type(s)
   * @param {*} transferData
   * @param {*} ttArray Selected transfer types (purposes)
   * @param {*} statusArray
   */
  const filterTableData = (messages, checkedMessageTypes) => {
    if (checkedMessageTypes.length) {
      messages = messages
        .filter((f) => f.type)
        .filter((f) => checkedMessageTypes.indexOf(f.type.toLowerCase()) >= 0);
    }
    return messages;
  };

  /**
   * This is a workaround function for table sorting in the mobile view
   */
  const sortTable = () => {
    setOrder(!sortOrder);
    const sortData = [
      ...tableData.sort((a, b) => {
        if (!sortOrder) {
          return a.type < b.type ? -1 : 1;
        } else {
          return a.type > b.type ? -1 : 1;
        }
      }),
    ];
    setTableData(sortData);
  };

  /**
   * Selecting one message type filter
   * @param {*} isChecked boolean
   * @param {*} index number
   */
  const selectMessageType = (isChecked, index) => {
    const newAvailableMessageTypes = availableMessageTypes.map((m, i) => {
      if (i === index) {
        return {
          ...m,
          isChecked,
        };
      }
      return m;
    });

    setAvailableMessageTypes(newAvailableMessageTypes);
    setTableData(
      filterTableData(
        copyTableData,
        newAvailableMessageTypes.filter((f) => f.isChecked).map((m) => m.value)
      )
    );
  };

  /**
   * selecting all message type filters
   */
  const selectAllMessageTypes = () => {
    const newAvailableMessageTypes = availableMessageTypes.map((m, i) => {
      return {
        ...m,
        isChecked: true,
      };
    });

    setAvailableMessageTypes(newAvailableMessageTypes);
    setTableData(
      filterTableData(
        copyTableData,
        newAvailableMessageTypes.filter((f) => f.isChecked).map((m) => m.value)
      )
    );
  };

  /**
   * resetting all message types filters
   */
  const resetAllMessageTypes = () => {
    const newAvailableMessageTypes = availableMessageTypes.map((m, i) => {
      return {
        ...m,
        isChecked: false,
      };
    });

    setAvailableMessageTypes(newAvailableMessageTypes);
    setTableData(
      filterTableData(
        copyTableData,
        newAvailableMessageTypes.filter((f) => f.isChecked).map((m) => m.value)
      )
    );
  };

  /**
   * setting searchCriteria will trigger a refetch
   */
  const search = () => {
    setSearchCriteria(
      Object.assign({}, searchCriteria, buildOrClause(searchKey))
    );
  };

  /**
   * datepicker handler
   * @param {*} e
   */
  const selectRange = (e) => {
    const tzOffset = new Date().getTimezoneOffset();
    setRangeDate(e);

    const dateRange = e
      ? e.map((m) => {
          return moment(moment.utc(m).toDate())
            .subtract(tzOffset, "minute")
            .format("MM/DD/YYYY");
        })
      : [];

    if (dateRange.length > 0) {
      setDaysDiff(moment(dateRange[1]).diff(moment(dateRange[0]), "days"));
      setSearchCriteria(
        Object.assign(
          {},
          {
            ...searchCriteria,
            createdAt_gte: moment(dateRange[0])
              .startOf("DAY")
              .toISOString(),
            createdAt_lte: moment(dateRange[1])
              .endOf("DAY")
              .toISOString(),
          }
        )
      );
    } else {
      // extract out start/end dates and leave out
      const { createdAt_gte, createdAt_lte, ...rest } = searchCriteria;
      setSearchCriteria(rest);
      setDaysDiff(null);
    }
  };

  /**
   * Handling the search input state
   * @param {*} e
   */
  const handleSearchInput = (e) => {
    const newValue = e?.target?.value ?? "";
    setSearchKey(newValue);

    if (e.key === "Enter" || !newValue) {
      search();
    }
  };

  const CalendarButton = ({ days }) => {
    let daysDescription;
    switch (days) {
      case 0:
        daysDescription = "Today";
        break;
      case 1:
        daysDescription = "Yesterday";
        break;
      case 7:
        daysDescription = "Last 7 days";
        break;
      default:
        daysDescription = null;
        break;
    }
    const resetData = () => {
      const { createdAt_gte, createdAt_lte, ...rest } = searchCriteria;
      setSearchCriteria(rest);
      setDaysDiff(null);
    };
    return (
      <>
        {daysDescription && (
          <div className="select-filter-item calendar" onClick={resetData}>
            <span>{daysDescription}</span>
            <img src={closeIcon} alt="" />
          </div>
        )}
      </>
    );
  };

  /**
   * handling client message data from GraphQL
   */
  useEffect(() => {
    if (allClientMessages && allClientMessages.length > 0) {
      setTableData(allClientMessages);
      setCopyTableData(allClientMessages);

      setAvailableMessageTypes(
        [
          ...new Set(
            allClientMessages
              .filter((f) => f.type)
              .map((m) => m.type.toLowerCase())
          ),
        ].map((m) => {
          return {
            label: m.split("_").join(" "),
            value: m,
            isChecked: false,
          };
        })
      );
    } else {
      setTableData([]);
      setCopyTableData([]);
      setAvailableMessageTypes([]);
    }
  }, [allClientMessages]);

  /**
   * rendering
   */
  return (
    <div className="message__container-outer">
      <div className="message__container">
        <h6 className="message__page-title">Messages</h6>
        <div className="message__search-container">
          {/* search input */}
          <div className="message__search-form-group">
            <input
              type="text"
              placeholder="Enter address or name"
              onKeyUp={(e) => handleSearchInput(e)}
            />
            <img src={searchIcon} alt="" onClick={search} />
          </div>

          {/* search button */}
          <button className="message__search-btn" onClick={search}>
            Search
          </button>

          {/* date picker */}
          <DatePicker.RangePicker
            size="small"
            onChange={selectRange}
            className="message__range-picker"
            value={rangeDate}
            format={"MM/DD/YYYY"}
            ranges={DateRanges}
          />

          {/* message type selection */}
          <OnOutsideClick
            onOutsideClick={() => setShowMessageTypes(false)}
            display="contents"
          >
            <div className="dropdown">
              <button
                className="dropdown-btn"
                onClick={() => setShowMessageTypes(!showMessageTypes)}
              >
                <span>Message Type</span> <img src={dropdownIcon} alt="" />
              </button>
              <div
                className={`dropdown-menu ${showMessageTypes ? "show" : ""}`}
              >
                <div className="disktop-filter">
                  <p className="filter-title">Filter By</p>
                  <div className="filter-container">
                    {availableMessageTypes.map((mt, index) => {
                      return (
                        <label className="filter-item" key={mt.label}>
                          <input
                            type="checkbox"
                            checked={mt.isChecked}
                            onChange={(e) =>
                              selectMessageType(e.target.checked, index)
                            }
                          />
                          <span style={{ textTransform: "capitalize" }}>
                            {mt.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="filter-footer">
                    <button onClick={selectAllMessageTypes}>Select All</button>
                    <button onClick={resetAllMessageTypes}>Reset</button>
                  </div>
                </div>
              </div>
            </div>
          </OnOutsideClick>
        </div>

        <div className="message__filter-container">
          <img
            src={sortIconAsc}
            alt="sortIconAsc"
            hidden={sortOrder}
            className="message__mobile-short-icon"
            onClick={() => sortTable()}
          />
          <img
            src={sortIconDsc}
            alt="sortIconDsc"
            hidden={!sortOrder}
            className="message__mobile-short-icon"
            onClick={() => sortTable()}
          />
          <div className="dropdown">
            <button
              className="dropdown-btn"
              onClick={() => setShowMobileFilter(!showMobileFilter)}
            >
              <span>Message Type</span> <img src={dropdownIcon} alt="" />
            </button>
            <div className={`dropdown-menu ${showMobileFilter ? "show" : ""}`}>
              <div
                className="mobile-backdrop"
                onClick={() => setShowMobileFilter(!showMobileFilter)}
              ></div>

              {/* mobile filter container */}
              <div className="mobile-filter-container">
                <div className="top-line"></div>
                <p className="filter-title">Filter By Message Type</p>
                <div className="filter-container">
                  {availableMessageTypes.map((mt, index) => {
                    return (
                      <label className="filter-item" key={mt.label}>
                        <input
                          type="checkbox"
                          checked={mt.isChecked}
                          onChange={(e) =>
                            selectMessageType(e.target.checked, index)
                          }
                        />
                        <span
                          style={{
                            textTransform: "capitalize",
                            fontWeight: "normal",
                          }}
                        >
                          {mt.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          {<CalendarButton days={daysDiff} />}
        </div>

        {/* standard and mobile */}
        <StandardView tableData={tableData} pdfOpenHandler={pdfOpenHandler} />
        <MobileView tableData={tableData} />
      </div>
    </div>
  );
};

export default MessageCenter;
